import styles from './styles.scss';
import { rm, NO_ELEMENT, scrollToTarget } from 'utils';
import { Button, LoaderButton } from 'components/Button';
import { useToggle } from 'hooks';
import { ChangeEvent, useEffect, useRef, useReducer } from 'react';
import {
  reducer,
  isValid,
  productChanged,
  getReasonOptions,
  reasonChange,
  claimsInitialState
} from './ClaimsHelper';
import { postSendClaims } from 'services/ApiCustomerSupportFormService';
import ExtendedInfo from 'views/CustomerPage/OrderHistory/ExtendedInfo';
import { DisplayMode } from 'views/CustomerPage/OrderHistory/OrderLine';
import {
  BlueSectionContainer,
  ErrorContainer,
  SubmitContainer,
  RadioBtnContainer
} from '../Components/SectionContainer/SectionContainer';
import { Address } from '../Components/Address/Address';
import { DynamicImageUploader } from '../Components/ImageUpload/DynamicImageUploader';
import {
  addressChanged,
  addressLookupValidationError,
  contactOptionChanged,
  OrderFormProps
} from '../Utils';
import { RadioButton, TextArea } from 'components/Shared/Form';
import { IBasicResponse } from 'models/IBasicResponse';
import MessageView from '../Components/MessageView';
import { useDispatch } from 'react-redux';
import { GtmCustomerSupportFormSubmit } from 'store/googleTagManager/actions';
import { FormType } from 'models/FormType';
import { CustomerContact } from '../Components/CustomerContact/CustomerContact';

const MSG_MAX_LENGTH = 1000;
const MAX_IMAGE_COUNT = 5;

export const ClaimsForm = ({ order, error, returnUrl, onSuccess }: OrderFormProps) => {
  const anchorRef = useRef(NO_ELEMENT);
  const scrollToTopIfNeeded = scrollToTarget(anchorRef);
  const [internalState, internalDispatch] = useReducer(reducer, claimsInitialState);
  const [addressChange, toggleAddressChange] = useToggle(false);
  const [loading, toggleLoading] = useToggle(false);
  const reduxDispatch = useDispatch();

  useEffect(() => {
    if (order) {
      internalDispatch({ type: 'address', value: order?.deliveryAddress });
      internalDispatch({ type: 'email', value: order?.email });
    }
    if (error) {
      internalDispatch({
        type: 'hasError',
        value: true
      });
    }
  }, [error, order]);

  useEffect(() => {
    internalDispatch({ type: 'addressError', value: '' });
  }, [addressChange]);

  if (internalState.hasError) {
    return (
      <MessageView
        title={rm('CustomerPage_Return_Error_Title')}
        message={rm('CustomerPage_Return_Error_Msg')}
        returnUrl={returnUrl}
      />
    );
  }

  if (order?.orderNumber === '' || order?.orderNumber === undefined) {
    return (
      <MessageView
        title={rm('CustomerPage_Empty_Return_Title')}
        message={rm('CustomerPage_Empty_Return_Info')}
        returnUrl={returnUrl}
      />
    );
  }

  const { address, email, description, images, reason, contactOption, products } = internalState;

  const completeInternal = () => {
    toggleLoading();
    internalDispatch({ type: 'generalError', value: '' });
    if (isValid(internalState, addressChange, internalDispatch)) {
      if (address && email && order) {
        return postSendClaims({
          address,
          email,
          orderNumber: order.orderNumber,
          description,
          files: images.map(img => img.imageUrl),
          reason: reason.replaceAll(' ', ''), // Enable customer service to search for words without spaces
          products: products.filter(p => p.quantity > 0), // Remove those with quantity 0
          hash: order.hash,
          customerContactOption: contactOption
        })
          .then(() => {
            toggleLoading();
            onSuccess();
            scrollToTopIfNeeded();
            reduxDispatch(GtmCustomerSupportFormSubmit(FormType.Claims));
          })
          .catch((response: IBasicResponse) => {
            toggleLoading();
            internalDispatch({ type: 'generalError', value: response.message[0] });
          });
      }
    }

    toggleLoading();
    return Promise.resolve();
  };

  const reasonIsChecked = (value: string) => {
    return value === internalState.reason;
  };

  const handleMsgInput = (_event: ChangeEvent<HTMLTextAreaElement>, newValue: string) => {
    internalDispatch({
      type: 'description',
      value: newValue
    });
    internalDispatch({
      type: 'descriptionError',
      value: ''
    });
  };

  return (
    <>
      <h3 className={styles.subHeader}>{rm('customerpage_returns_products_header')}</h3>
      {order && (
        <ExtendedInfo
          order={order}
          displayMode={DisplayMode.Claims}
          returnProductChanged={p => productChanged(p, internalDispatch)}
          className={styles.products}
        />
      )}
      {internalState.productsError && (
        <BlueSectionContainer error={true}>{internalState.productsError}</BlueSectionContainer>
      )}
      <h3 className={styles.subHeader}>{rm('customerpage_returns_address_header')}</h3>
      <BlueSectionContainer error={internalState.addressError}>
        <Address
          address={internalState.address}
          email={internalState.email}
          onChange={(address, email) => addressChanged(address, email, internalDispatch)}
          changeMode={addressChange}
          toggleChange={toggleAddressChange}
          validationError={() => {
            addressLookupValidationError(internalDispatch);
          }}
        />
      </BlueSectionContainer>
      {internalState.addressError && <ErrorContainer>{internalState.addressError}</ErrorContainer>}
      <h3 className={styles.subHeader}>{rm('customerpage_claims_reasonarea_header')}</h3>
      <BlueSectionContainer error={internalState.reasonError}>
        <strong>{rm('customerpage_claims_reason_header')}</strong>
        <RadioBtnContainer>
          {getReasonOptions().map(item => (
            <RadioButton
              key={item.id}
              value={item.value}
              label={item.label}
              name="reasons-radio-button"
              checked={reasonIsChecked(item.value)}
              onChange={val => reasonChange(val, internalDispatch)}
            />
          ))}
        </RadioBtnContainer>
      </BlueSectionContainer>
      {internalState.reasonError && <ErrorContainer>{internalState.reasonError}</ErrorContainer>}
      <BlueSectionContainer error={internalState.descriptionError}>
        <strong>{rm('customerpage_claims_description_header')}</strong>
        <TextArea
          onChange={handleMsgInput}
          textlimit={MSG_MAX_LENGTH}
          value={internalState.description}
        />
        <p id={styles.msgLength}>{`(${internalState.description.length}/${MSG_MAX_LENGTH})`}</p>
      </BlueSectionContainer>
      {internalState.descriptionError && (
        <ErrorContainer>{internalState.descriptionError}</ErrorContainer>
      )}
      <BlueSectionContainer error={internalState.imagesError}>
        <div className={styles.msgHeader}>
          <strong>{rm('customerpage_returns_images_header')}</strong>
          <p>{rm('CustomerService_Return_Optional_Label')}</p>
        </div>
        <DynamicImageUploader
          images={internalState.images}
          maxImageCount={MAX_IMAGE_COUNT}
          dispatch={internalDispatch}
        />
      </BlueSectionContainer>
      {internalState.imagesError && <ErrorContainer>{internalState.imagesError}</ErrorContainer>}
      <CustomerContact
        onChange={val => contactOptionChanged(val, internalDispatch)}
        error={internalState.contactOptionError}
        contact={internalState.contactOption}
      />
      <SubmitContainer>
        <LoaderButton appearance="whitePillPrimary" onClick={completeInternal} loading={loading}>
          {loading ? '' : rm('customerpage_returns_submit')}
        </LoaderButton>
        <Button appearance="whitePill" href={returnUrl}>
          {rm('customerpage_returns_cancel')}
        </Button>
      </SubmitContainer>
      {internalState.generalError && (
        <BlueSectionContainer error={true}>{internalState.generalError}</BlueSectionContainer>
      )}
    </>
  );
};
